import * as stylex from "@stylexjs/stylex";
import { useId, type ReactNode } from "react";
import { X } from "lucide-react";
import "@/design/tokens.stylex";
import { colors, motion, radii, shadows, space, text } from "@/design/tokens.stylex";
import { IconButton } from "./Button";
import { useModalFocus } from "./useModalFocus";

// A dialog over the dimmed page it belongs to (Mercury 05, 08): a title and
// close, the body, and the buttons pinned at the foot, primary on the right.

const rise = "xef0u7w-B";
const styles = {
  overlay: {
    kVAEAm: "xixxii4",
    kpwlN0: "x10a8y8t",
    kY2c9j: "xa9119d",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmVPX3: "x1wmoasd",
    kWkggS: "x1q0ji57",
    $$css: true
  },
  dialog: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kzqmXN: "xh8yej3",
    kskxy: "xqui1pq",
    kWkggS: "x1h1f4i9",
    kaIpWk: "xhrzdqn",
    kGVxlE: "x1n1ksyb",
    kKVMdj: "x3wb73n",
    k44tkh: "x1gxywl9",
    kyAemX: "xt2ab0p",
    $$css: true
  },
  sm: {
    ks0D6T: "x1lr1uin",
    $$css: true
  },
  md: {
    ks0D6T: "x1lwm3bp",
    $$css: true
  },
  lg: {
    ks0D6T: "xapjn7j",
    $$css: true
  },
  header: {
    k1xSpc: "x78zum5",
    kGNEyG: "x1cy8zhl",
    kOIVth: "x5vihii",
    kLKAdn: "xkkwws9",
    kg3NbH: "xf8hsz2",
    kGO01o: "xxgjxbq",
    $$css: true
  },
  titles: {
    kUk6DE: "x98rzlu",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  title: {
    kogj98: "x1ghz6dp",
    kLKAdn: "x67pdoy",
    kGuDYH: "xxmgfec",
    k63SB2: "xk50ysn",
    kMwMTN: "xgwkh9f",
    $$css: true
  },
  description: {
    keoZOQ: "x17bo9yi",
    k1K539: "xat24cr",
    kGuDYH: "xtu3avg",
    kMwMTN: "x1e3avs9",
    $$css: true
  },
  body: {
    kUk6DE: "x98rzlu",
    kAzted: "x2lwn1j",
    kORKVm: "x1odjw0f",
    kg3NbH: "xf8hsz2",
    kGO01o: "x1x82rsn",
    $$css: true
  },
  footer: {
    k1xSpc: "x78zum5",
    kjj79g: "x13a6bvl",
    kGNEyG: "x6s0dn4",
    kOIVth: "x166cprz",
    k8WAf4: "x9end0a",
    kg3NbH: "xf8hsz2",
    kEafiO: "x178xt8z",
    kPef9Z: "x13fuv20",
    kLZC3w: "xq9jyqv",
    $$css: true
  }
};
export interface ModalProps {
  title: string;
  description?: ReactNode;
  /** Buttons pinned to the foot: Cancel, then the one primary action. */
  footer?: ReactNode;
  children: ReactNode;
  onClose: () => void;
  size?: "sm" | "md" | "lg";
}
export default function Modal({
  title,
  description,
  footer,
  children,
  onClose,
  size = "lg"
}: ModalProps) {
  const titleId = useId();
  const dialogRef = useModalFocus<HTMLDivElement>(onClose);
  return <div className="xixxii4 x10a8y8t xa9119d x78zum5 x6s0dn4 xl56j7k x1wmoasd x1q0ji57" onMouseDown={event => {
    if (event.target === event.currentTarget) onClose();
  }}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} {...stylex.props(styles.dialog, styles[size])}>
        <div className="x78zum5 x1cy8zhl x5vihii xkkwws9 xf8hsz2 xxgjxbq">
          <div className="x98rzlu xeuugli">
            <h2 id={titleId} className="x1ghz6dp x67pdoy xxmgfec xk50ysn xgwkh9f">
              {title}
            </h2>
            {description && <p className="x17bo9yi xat24cr xtu3avg x1e3avs9">{description}</p>}
          </div>
          <IconButton aria-label="Close" onClick={onClose} size="sm">
            <X aria-hidden size={16} />
          </IconButton>
        </div>
        <div className="x98rzlu x2lwn1j x1odjw0f xf8hsz2 x1x82rsn">{children}</div>
        {footer && <div className="x78zum5 x13a6bvl x6s0dn4 x166cprz x9end0a xf8hsz2 x178xt8z x13fuv20 xq9jyqv">{footer}</div>}
      </div>
    </div>;
}